import type { Plugin } from 'unified';
import type { Root, Element, Text, Parent } from 'hast';
import { visit } from 'unist-util-visit';

export interface CommentRange {
  id: string;
  start: number;
  end: number;
}

/**
 * Rehype plugin that wraps character ranges (in rendered plain-text space) with
 * <mark data-comment-id="..."> elements. Ranges are offsets into the concatenated
 * text content of all HAST text nodes — the same value as element.textContent in
 * the DOM, so capture and rendering use the same coordinate system.
 */
export const rehypeMarkRanges: Plugin<[CommentRange[]], Root> = (ranges) => {
  const active = (ranges ?? []).filter((r) => r.start >= 0 && r.end > r.start);
  if (!active.length) return () => {};

  return (tree) => {
    // Collect all text nodes in document order with their start/end offsets
    const entries: Array<{ node: Text; parent: Parent; start: number; end: number }> = [];
    let pos = 0;

    visit(tree, 'text', (node: Text, _index, parent) => {
      entries.push({ node, parent: parent as Parent, start: pos, end: pos + node.value.length });
      pos += node.value.length;
    });

    // Process ranges sorted by descending start so earlier mutations don't shift later indices
    const sorted = [...active].sort((a, b) => b.start - a.start);

    for (const range of sorted) {
      const overlapping = entries.filter((e) => e.start < range.end && e.end > range.start);
      if (!overlapping.length) continue;

      // Process in reverse document order to preserve sibling indices within a parent
      for (let i = overlapping.length - 1; i >= 0; i--) {
        const entry = overlapping[i]!;
        const { node, parent } = entry;

        const localStart = Math.max(range.start, entry.start) - entry.start;
        const localEnd   = Math.min(range.end,   entry.end)   - entry.start;

        const before = node.value.slice(0, localStart);
        const marked = node.value.slice(localStart, localEnd);
        const after  = node.value.slice(localEnd);

        const markEl: Element = {
          type: 'element',
          tagName: 'mark',
          properties: { dataCommentId: range.id },
          children: [{ type: 'text', value: marked } as Text],
        };

        const replacements: Array<Text | Element> = [];
        if (before) replacements.push({ type: 'text', value: before } as Text);
        replacements.push(markEl);
        if (after)  replacements.push({ type: 'text', value: after } as Text);

        const idx = parent.children.indexOf(node as never);
        if (idx !== -1) parent.children.splice(idx, 1, ...replacements as never[]);
      }
    }
  };
};
